import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, IconButton, List, ListItem, ListItemText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../services/api';

interface Signer {
    name: string;
    email: string;
}

const Signature: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [uploadUrl, setUploadUrl] = useState<string>('');
    const [signers, setSigners] = useState<Signer[]>([{ name: '', email: '' }]);
    const [tenantId, setTenantId] = useState<string>('');
    const [tenants, setTenants] = useState<Array<any>>([]);
    const [products, setProducts] = useState<Array<any>>([]);
    const [productId, setProductId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<string>('');

    useEffect(() => {
        // fetch tenants and products for selection
        (async () => {
            try {
                const res = await api.get('/api/tenants/list');
                setTenants(res.data || []);
                const p = await api.get('/api/products');
                setProducts(p.data || []);
            } catch (err) {
                // ignore if not authorized
            }
        })();
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) return setMessage('Envie um arquivo primeiro');
        try {
            setLoading(true);
            const form = new FormData();
            form.append('file', file);
            const res = await api.post('/api/signatures/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
            setUploadUrl(res.data.url);
            setMessage('Upload concluído');
        } catch (err: any) {
            setMessage('Erro ao enviar arquivo');
        } finally {
            setLoading(false);
        }
    };

    const updateSigner = (index: number, key: keyof Signer, value: string) => {
        const s = [...signers];
        s[index] = { ...s[index], [key]: value };
        setSigners(s);
    };

    const addSigner = () => setSigners([...signers, { name: '', email: '' }]);
    const removeSigner = (index: number) => setSigners(signers.filter((_, i) => i !== index));

    const handleCreate = async () => {
        if (!tenantId) return setMessage('Selecione um tenant');
        if (!uploadUrl) return setMessage('Faça upload do documento primeiro');
        try {
            setLoading(true);
            const body: any = { tenantId, documentUrl: uploadUrl, signers };
            if (productId) body.productId = productId;
            const res = await api.post('/api/signatures', body);
            setMessage('Pedido de assinatura criado: ' + (res.data.id || 'ok'));
        } catch (err) {
            setMessage('Erro ao criar pedido');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ padding: 2 }}>
            <Paper sx={{ padding: 2 }}>
                <Typography variant="h6">Criar Assinatura</Typography>

                <Box sx={{ marginTop: 2 }}>
                    <TextField
                        select
                        label="Tenant"
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value)}
                        SelectProps={{ native: true }}
                        fullWidth
                        size="small"
                    >
                        <option value="">-- selecione --</option>
                        {tenants.map((t: any) => (
                            <option key={t._id} value={t._id}>{t.nomeFantasia || t.nameFantasia || t.name || t.company || t._id}</option>
                        ))}
                    </TextField>
                </Box>

                <Box sx={{ marginTop: 2 }}>
                    <TextField
                        select
                        label="Produto (opcional)"
                        value={productId}
                        onChange={(e) => setProductId(e.target.value)}
                        SelectProps={{ native: true }}
                        fullWidth
                        size="small"
                    >
                        <option value="">-- nenhum --</option>
                        {products.map((p: any) => (
                            <option key={p._id} value={p._id}>{p.name || p._id}</option>
                        ))}
                    </TextField>
                </Box>

                <Box sx={{ marginTop: 2 }}>
                    <input type="file" onChange={handleFileChange} />
                    <Button sx={{ ml: 2 }} variant="contained" onClick={handleUpload} disabled={loading}>Upload</Button>
                </Box>

                {uploadUrl && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Arquivo: {
                                (() => {
                                    try {
                                        const base = (api.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:8081').replace(/\/api\/?$/, '');
                                        const full = uploadUrl.startsWith('http') ? uploadUrl : `${base}${uploadUrl}`;
                                        return <a href={full} target="_blank" rel="noreferrer">{full}</a>;
                                    } catch (e) {
                                        return <span>{uploadUrl}</span>;
                                    }
                                })()
                            }
                        </Typography>
                    </Box>
                )}

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1">Signatários</Typography>
                    <List>
                        {signers.map((s, i) => (
                            <ListItem key={i} secondaryAction={
                                <IconButton edge="end" onClick={() => removeSigner(i)}>
                                    <DeleteIcon />
                                </IconButton>
                            }>
                                <ListItemText primary={
                                    <>
                                        <TextField placeholder="Nome" value={s.name} onChange={(e) => updateSigner(i, 'name', e.target.value)} size="small" sx={{ mr: 1 }} />
                                        <TextField placeholder="Email" value={s.email} onChange={(e) => updateSigner(i, 'email', e.target.value)} size="small" />
                                    </>
                                } />
                            </ListItem>
                        ))}
                    </List>
                    <Button startIcon={<AddIcon />} onClick={addSigner}>Adicionar signatário</Button>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button variant="contained" color="primary" onClick={handleCreate} disabled={loading}>Enviar para assinatura</Button>
                    <Button variant="outlined" onClick={() => { setFile(null); setUploadUrl(''); setMessage(''); }}>Limpar</Button>
                </Box>

                {message && <Box sx={{ mt: 2 }}><Typography variant="body2">{message}</Typography></Box>}
            </Paper>
        </Box>
    );
};

export default Signature;
